import {
  Anchor,
  Badge,
  Button,
  Card,
  Icon,
  MenuListItem,
  MenuListItemRegularText,
  NavItem,
  Text
} from '@albert-io/atomic';
import {removeQueryParams} from 'client/history';
import {Map} from 'immutable';
import React, {createRef, useEffect, useState} from 'react';
import {Link} from 'react-router';

import sessionStore from 'client/Session/SessionStore';

import AssignmentsFoldersDropdown from '../AssignmentsFoldersDropdown.react';
import {getTeacherHasClassSubjects} from '../GlobalNav.queries';
import {getNavigationLinksForPath} from '../navLinks';

import BetaNavButton from './BetaNavButton';

interface MainLinksProps {
  pathname: string;
}

const MainLinks = ({pathname}: MainLinksProps) => {
  const [isTeacherWithClassSubjects, setIsTeacherWithClassSubjects] = useState(false);

  useEffect(() => {
    if (sessionStore.isTeacher()) {
      (async () => {
        const teacherHasClassSubjects = await getTeacherHasClassSubjects();
        setIsTeacherWithClassSubjects(teacherHasClassSubjects);
      })();
    }
  }, [setIsTeacherWithClassSubjects]);

  const getIsMatchingRoute = (link) => {
    const linkRoute = link.get('route');
    const routesArr = link.get('routesArr');
    let isMatchingRoute = false;

    if (routesArr) {
      isMatchingRoute = routesArr.some((route) => pathname.startsWith(route));
    } else if (
      /**
       * This insures that the Subjects header link will contain the active class
       * regardless of which group/subject combination is rendered. It will be replaced soon.
       */
      (link.has('matchingRoutes') && link.get('matchingRoutes').contains(pathname)) ||
      linkRoute === pathname ||
      pathname.startsWith(linkRoute) ||
      (link.has('matchingRoutes') &&
        link
          .get('matchingRoutes')
          .filter((path) => pathname.indexOf(path) === 0)
          .count() > 0)
    ) {
      isMatchingRoute = true;
    }
    return isMatchingRoute;
  };

  const links = getNavigationLinksForPath(pathname, {
    isTeacherWithClassSubjects
  });

  return (
    <nav className='global-nav__main' aria-label='main'>
      <ul className='global-nav__main-list'>
        {/* @ts-ignore link is defined */}
        {links.map((link: Map<string, any>) => {
          if (link.has('dropdownLinks')) {
            return <MainLinksDropdown key={link.get('key')} link={link} pathname={pathname} />;
          }

          const isMatchingRoute = getIsMatchingRoute(link);
          const props = {
            [link.get('hardLink', false) ? 'href' : 'to']: link.get('route')
          };
          const LinkElement = link.get('hardLink', false) ? 'a' : Link;

          if (link.get('key') === 'assignments') {
            return (
              // @ts-ignore
              <AssignmentsFoldersDropdown
                key={link.get('key')}
                link={link}
                isMatchingRoute={isMatchingRoute}
                pathname={pathname}
                {...props}
              />
            );
          }
          if (link.get('name') === 'Launch Assignment Builder') {
            return (
              <li key={link.get('key')} className='u-white-space_nowrap'>
                {/* @ts-ignore special case for assignment builder button (which will soon be deprecated) */}
                <NavItem stateless as={LinkElement} active={isMatchingRoute}>
                  <BetaNavButton link={link} pathname={pathname} />
                </NavItem>
              </li>
            );
          }

          return (
            <li key={link.get('key')}>
              {/* @ts-ignore */}
              <NavItem
                {...props}
                icon={link.get('icon', null)}
                as={LinkElement}
                active={isMatchingRoute}
              >
                {link.get('name')}
              </NavItem>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

interface DropdownProps {
  link: Map<string, any>;
  pathname: string;
}

const MainLinksDropdown = ({link, pathname}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = createRef<HTMLLIElement>();
  const navItemRef = createRef<HTMLLIElement>();
  const dropdownRef = createRef<HTMLDivElement>();

  const handleToggle = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
    navItemRef.current?.focus();
  };

  const onClickOutsideComponent = (e) => {
    if (isOpen && !containerRef.current?.contains(e.target)) {
      handleClose();
    }
  };

  const handleNavItemKeyDown = (e) => {
    // Enter Key
    if (e.key === 'Enter') {
      handleToggle();
    }
    // ESC Key
    if (e.key === 'Escape') {
      handleClose();
    }
    // Right/Down Arrow Keys
    if (['ArrowRight', 'ArrowDown'].includes(e.key) && isOpen) {
      e.preventDefault();
      if (dropdownRef.current) {
        (dropdownRef.current.querySelector('.global-nav__dropdown-item') as HTMLElement).focus();
      }
    }
  };

  const handleDropdownKeyDown = (e) => {
    // ESC & Tab Key
    if (
      e.key === 'Escape' ||
      (e.key === 'Tab' && dropdownRef.current?.lastChild === e.target.parentNode)
    ) {
      handleClose();
    }
    // Right/Down Arrow Keys
    if (['ArrowRight', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const {nextSibling, parentNode} = e.target.parentNode;
      if (nextSibling) {
        nextSibling.firstChild.focus();
      } else {
        parentNode.querySelector('.global-nav__dropdown-item').focus();
      }
    }
    // Left/Up Arrow Keys
    if (['ArrowLeft', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const {previousSibling, parentNode} = e.target.parentNode;
      if (previousSibling) {
        previousSibling.firstChild.focus();
      } else {
        parentNode.lastChild.firstChild.focus();
      }
    }
  };

  useEffect(() => {
    global.addEventListener('click', onClickOutsideComponent);
    return () => {
      global.removeEventListener('click', onClickOutsideComponent);
    };
  }, [onClickOutsideComponent]);

  return (
    <li ref={containerRef} className='global-nav__has-dropdown'>
      <NavItem
        to=''
        ref={navItemRef}
        as='span'
        // tabIndex={0}
        aria-haspopup
        aria-expanded={isOpen}
        // @ts-ignore
        onKeyDown={handleNavItemKeyDown}
      >
        {link.get('name')}
        <Icon icon='caret-down' className='u-mar-lr_1' />
        {link.get('name') === 'Assignments' && (
          <Badge emphasis='bold' size='small' className='global-nav__beta-badge u-mar-l_1'>
            <Text color='primary-inverse' className='u-text-transform_uppercase u-font-weight_900'>
              Beta
            </Text>
          </Badge>
        )}
      </NavItem>
      <Card
        ref={dropdownRef}
        // @ts-ignore
        hidden={!isOpen}
        className='global-nav__main-link-dropdown'
        shadow='default'
        onKeyDown={handleDropdownKeyDown}
      >
        {link.get('dropdownLinks').map((subLink) => {
          const buttons = {
            'Create Assignment Beta': (
              <>
                <Button
                  as={Link}
                  to={`${subLink.get('route')}`}
                  onClick={() => {
                    if (pathname.includes('/create-assignment')) {
                      removeQueryParams('draftId');
                      setTimeout(() => {
                        global.window.location.reload();
                      }, 100);
                    }
                  }}
                >
                  <Icon icon='plus' className='u-mar-r_1' />
                  Create Assignment (Beta)
                </Button>
                <div className='a-divider u-mar-t_2 u-mar-b_1' />
              </>
            ),
            'View Drafts': (
              <>
                <MenuListItem as={Link} to={subLink.get('route')}>
                  <MenuListItemRegularText>
                    <Icon icon={['far', 'file-alt']} className='u-mar-r_2' />
                    {subLink.get('name')}
                  </MenuListItemRegularText>
                </MenuListItem>
                <div className='a-divider u-mar-tb_1' />
              </>
            ),
            'Give Beta Feedback': (
              <MenuListItem
                as={Anchor}
                href={subLink.get('href')}
                target='_blank'
                rel='noopener noreferrer'
                noUnderline
              >
                <MenuListItemRegularText>
                  <Icon icon={['far', 'arrow-up-right-from-square']} className='u-mar-r_2' />
                  {subLink.get('name')}
                </MenuListItemRegularText>
              </MenuListItem>
            )
          };
          return buttons[subLink.get('name')];
        })}
      </Card>
    </li>
  );
};

export default MainLinks;
